// @ts-nocheck
import PropTypes from 'prop-types';
import ServiceCard from '../ServiceCard/ServiceCard';
import LineItemCard from '../LineItemCard/LineItemCard';
import DayHeader from '../DayHeader/DayHeader';
import { useOnlineStatus } from '../../context/OnlineStatusContext/OnlineStatusContext';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getCheckoutServicesForDay } from '../../helpers/accommodation';
import { getCheckoutLineItemsForDay } from '../../../../components/LineItem/Display/helpers/accommodation';
import {
  addTravelDays,
  formatTravelDate,
  normalizeTravelDate
} from '../../../../utils/travelDate';
import './Itinerary.css';

const animateHorizontalScroll = (element, targetLeft, durationMs = 500) => {
  if (!element) return () => {};

  const startLeft = element.scrollLeft;
  const delta = targetLeft - startLeft;

  const startTime = performance.now();

  const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  let rafId = null;

  const step = (now) => {
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / durationMs);
    const eased = easeInOutCubic(t);
    element.scrollLeft = startLeft + delta * eased;

    if (t < 1) {
      rafId = requestAnimationFrame(step);
    }
  };

  rafId = requestAnimationFrame(step);

  return () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
  };
};

const mapTripDays = (
  day = {},
  detailedDay = {},
  enableRefactoredServices = false
) => {
  const { tripdetaileddayservice = [], description } = detailedDay;
  const descriptionText = enableRefactoredServices
    ? day?.finaldescription || day?.description || ''
    : description || day?.description || '';

  return {
    tripdayid: day?.tripdayid,
    daynumber: day?.daynumber,
    title: day?.title,
    description: descriptionText,
    image: day?.tripdayimage?.[0]?.imageurl,
    services: tripdetaileddayservice
  };
};

const getTripDayNumber = (day = {}, index = 0) => {
  const dayNumber = Number(day?.daynumber);

  return Number.isFinite(dayNumber) && dayNumber > 0 ? dayNumber : index + 1;
};

const getTripDuration = (tripData = {}) => {
  const duration = Number(tripData?.duration);

  if (Number.isFinite(duration) && duration > 0) {
    return duration;
  }

  return Array.isArray(tripData?.tripday) ? tripData.tripday.length : 0;
};

const getDateTabDayLabel = (dayNumber, t) =>
  `${t('common:day', 'Day')} ${dayNumber}`;

const getDateTabAriaLabel = (day = {}, t) => {
  const dayLabel = getDateTabDayLabel(day?.dayNumber, t);
  const details = [day?.dayLabel, day?.date].filter(Boolean).join(', ');

  return details && details !== dayLabel ? `${dayLabel}: ${details}` : dayLabel;
};

const generateDateTabs = (tripData, t) => {
  const duration = getTripDuration(tripData);

  if (!duration) {
    return [];
  }

  const tripStartDate = tripData?.startdate
    ? normalizeTravelDate(tripData.startdate)
    : null;
  const todayTravelDate = tripStartDate
    ? normalizeTravelDate(new Date())
    : null;
  const days = [];

  for (let i = 0; i < duration; i++) {
    const dayNumber = getTripDayNumber(tripData?.tripday?.[i], i);
    const currentDate = tripStartDate
      ? addTravelDays(tripStartDate, dayNumber - 1)
      : null;
    const tomorrowTravelDate = todayTravelDate
      ? addTravelDays(todayTravelDate, 1)
      : null;
    const isCurrentDay = Boolean(
      currentDate && currentDate === todayTravelDate
    );
    const isNextDay = Boolean(
      currentDate && currentDate === tomorrowTravelDate
    );
    const isPastDay = Boolean(todayTravelDate && currentDate < todayTravelDate);

    const dayLabel = currentDate
      ? isCurrentDay
        ? t('common:today', 'Today')
        : isNextDay
          ? t('common:tomorrow', 'Tomorrow')
          : formatTravelDate(currentDate, 'EEE')
      : getDateTabDayLabel(dayNumber, t);

    days.push({
      dayNumber,
      dayLabel,
      date: currentDate ? formatTravelDate(currentDate, 'MMM d') : '',
      fullDate: currentDate,
      isToday: isCurrentDay,
      isTomorrow: isNextDay,
      isPast: isPastDay,
      isFuture: !isPastDay && !isCurrentDay
    });
  }

  return days;
};

const Itinerary = ({ id, tripData = {}, ...rest }) => {
  const { t } = useTranslation(['common']);
  const { enableRefactoredServices } = useFlags();
  const scrollContainerRef = useRef(null);
  const dayContentTopRef = useRef(null);
  const hasMountedRef = useRef(false);
  const scrollAnimationCancelRef = useRef(null);
  const dateTabs = generateDateTabs(tripData, t);
  const todayIndex = dateTabs.findIndex((day) => day.isToday);
  const [selectedDay, setSelectedDay] = useState(
    todayIndex !== -1 ? todayIndex : 0
  );
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const { tripday = [], tripdetailedday = [], startdate } = tripData || {};

  const foundDetailedDay = tripdetailedday.find(
    (day) => day?.tripdayid === (tripday[selectedDay]?.tripdayid || 0)
  );
  const currentItinerary = mapTripDays(
    tripday[selectedDay],
    foundDetailedDay,
    enableRefactoredServices
  );
  const tripStartDate = startdate ? normalizeTravelDate(startdate) : null;

  // Get checkout services for the current day
  const currentDayDate = dateTabs[selectedDay]?.fullDate;
  const checkoutServices = currentDayDate
    ? getCheckoutServicesForDay(tripdetailedday, currentDayDate, tripStartDate)
    : [];

  const { isOnline } = useOnlineStatus();

  // Get line items for the current day (refactored structure)
  const currentDayLineItems = enableRefactoredServices
    ? (tripday[selectedDay]?.lineitem || []).sort(
        (a, b) => (a?.seq || 0) - (b?.seq || 0)
      )
    : [];

  // Get checkout LineItems for the current day (for accommodations)
  const checkoutLineItems =
    enableRefactoredServices && currentDayDate
      ? getCheckoutLineItemsForDay(tripday, currentDayDate, tripStartDate)
      : [];

  // Combine regular LineItems with checkout LineItems
  const allLineItemsForDay = enableRefactoredServices
    ? [...checkoutLineItems, ...currentDayLineItems]
    : [];

  // Handle scroll indicators
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setShowLeftGradient(scrollLeft > 10);
    setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  // Auto-scroll to selected day
  const scrollToSelectedDay = useCallback((dayIndex) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (scrollAnimationCancelRef.current) {
      scrollAnimationCancelRef.current();
      scrollAnimationCancelRef.current = null;
    }

    const dayButton = container.children[dayIndex];
    if (dayButton) {
      const containerWidth = container.clientWidth;
      const buttonLeft = dayButton.offsetLeft;
      const buttonWidth = dayButton.offsetWidth;
      const targetScrollLeft =
        buttonLeft - Math.max((containerWidth - buttonWidth) / 2, 0);

      const maxScrollLeft = container.scrollWidth - containerWidth;
      const clampedScrollLeft = Math.max(
        0,
        Math.min(targetScrollLeft, maxScrollLeft)
      );

      const previousScrollSnapType = container.style.scrollSnapType;
      const previousScrollBehavior = container.style.scrollBehavior;
      container.style.scrollSnapType = 'none';
      container.style.scrollBehavior = 'auto';

      scrollAnimationCancelRef.current = animateHorizontalScroll(
        container,
        clampedScrollLeft,
        500
      );

      window.setTimeout(() => {
        if (scrollContainerRef.current === container) {
          container.style.scrollSnapType = previousScrollSnapType;
          container.style.scrollBehavior = previousScrollBehavior;
        }
      }, 600);
    }
  }, []);

  // Handle day selection
  const handleDaySelect = (dayIndex) => {
    setSelectedDay(dayIndex);
  };

  // Set initial scroll indicators
  useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      handleScroll();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleScroll]);

  // Auto-scroll to selected day when it changes
  useEffect(() => {
    if (selectedDay !== null) {
      scrollToSelectedDay(selectedDay);
    }
  }, [selectedDay, scrollToSelectedDay]);

  // Scroll to the top of the newly selected day's content (skip initial mount)
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    const anchor = dayContentTopRef.current;
    if (!anchor) return;

    anchor.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, [selectedDay]);

  const servicesList = currentItinerary?.services?.map((service) => {
    const { servicetype = {}, servicefields = [] } = service;
    const serviceTypeFields = servicetype?.servicetypefield ?? [];
    const mappedServiceFields = servicefields?.map((field) => {
      const foundServiceTypeField = serviceTypeFields?.find(
        (item) =>
          item?.servicefield?.servicefieldid === field?.servicefieldid ||
          item?.servicefieldid === field?.servicefieldid
      );

      const key =
        foundServiceTypeField?.key ??
        foundServiceTypeField?.servicefield?.key ??
        '';

      return { ...field, key };
    });

    return {
      ...service,
      servicefields: mappedServiceFields,
      tripdetaileddayserviceid: service?.tripdetaileddayserviceid
    };
  });

  // Combine checkout services with regular services
  // Checkout services should appear FIRST
  const allServicesForDay = [...checkoutServices, ...(servicesList || [])];

  return (
    <div
      id={id}
      className={`itinerary ${!isOnline ? 'pwa-itinerary__offline-margin' : ''}`}
      {...rest}
    >
      {/* Sticky Date Navigation */}
      <div
        className={`itinerary__date-nav ${!isOnline ? 'itinerary__date-nav__offline-margin' : ''}`}
      >
        <div className='itinerary__date-scroll-container'>
          {/* Left gradient */}
          {showLeftGradient && (
            <div className='itinerary__date-gradient itinerary__date-gradient--left' />
          )}

          {/* Scrollable tabs container */}
          <div
            className='itinerary__date-tabs'
            ref={scrollContainerRef}
            onScroll={handleScroll}
          >
            {dateTabs.map((day, index) => {
              const isSelected = selectedDay === index;
              const { isToday } = day;
              const tabClasses = [
                'itinerary__date-tab',
                isSelected && 'itinerary__date-tab--active',
                isToday && 'itinerary__date-tab--today'
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  key={index}
                  className={tabClasses}
                  onClick={() => handleDaySelect(index)}
                  data-day={day?.dayNumber}
                  aria-label={getDateTabAriaLabel(day, t)}
                  aria-selected={isSelected}
                  tabIndex={0}
                >
                  <span className='itinerary__date-tab-day'>
                    {day?.dayLabel}
                  </span>
                  <span className='itinerary__date-tab-date'>{day?.date}</span>
                </button>
              );
            })}
          </div>

          {/* Right gradient */}
          {showRightGradient && (
            <div className='itinerary__date-gradient itinerary__date-gradient--right' />
          )}
        </div>
      </div>

      {/* Day Content */}
      <div className='itinerary__content'>
        <div ref={dayContentTopRef} className='itinerary__day-content-top' />
        <DayHeader
          dayNumber={currentItinerary?.daynumber || selectedDay + 1}
          title={currentItinerary.title}
          description={currentItinerary?.description}
          backgroundImage={currentItinerary?.image}
          badgeColor={'#ffffff'}
          textColor='#2c3e50'
        />

        {/* Activities List*/}
        <div className='itinerary__activities'>
          {enableRefactoredServices
            ? // NEW: Render LineItems (including checkout LineItems)
              allLineItemsForDay?.map((lineitem, index) => (
                <LineItemCard
                  key={lineitem?.lineitemid || index}
                  lineitem={lineitem}
                  rawstartdate={startdate}
                  daynumber={tripday[selectedDay]?.daynumber}
                  isCheckout={lineitem?.isCheckout}
                />
              ))
            : // LEGACY: Render Services
              allServicesForDay?.map((service, index) => (
                <ServiceCard
                  key={
                    service.isCheckout
                      ? `checkout-${service.originalServiceId}`
                      : service?.tripdetaileddayserviceid || index
                  }
                  service={service}
                  index={index}
                  tripid={tripData?.tripid}
                  tenantid={tripData?.tenant?.tenantid}
                  isCheckout={service?.isCheckout}
                />
              ))}
        </div>
      </div>
    </div>
  );
};

Itinerary.propTypes = {
  id: PropTypes.string,
  tripData: PropTypes.object,
  templateSettings: PropTypes.object
};

export default Itinerary;
